import { useEffect, useState } from 'react';

import * as THREE from 'three';

import { useStore } from '../../../pages/workspace/hooks.ts';

/**
 * Renders the polygon-flow reference image (uploaded plan) as a textured plane
 * slightly below the model, sized so that 1 image pixel corresponds to
 * `modelScale` meters. This recreates the layout the user had when
 * drawing polygons over the image during project creation.
 */
export function ReferenceImagePlane() {
  const { project, showReferenceImage } = useStore();
  const [texture, setTexture] = useState<THREE.Texture | null>(null);

  const url = project?.referenceImageUrl;
  const scale = project?.modelScale;

  useEffect(() => {
    if (!url) {
      setTexture(null);
      return;
    }
    const loader = new THREE.TextureLoader();
    loader.setCrossOrigin('anonymous');
    let cancelled = false;
    loader.load(
      url,
      (tex) => {
        if (cancelled) {
          tex.dispose();
          return;
        }
        tex.colorSpace = THREE.SRGBColorSpace;
        setTexture(tex);
      },
      undefined,
      (err) => console.error('Failed to load reference image:', err),
    );
    return () => {
      cancelled = true;
    };
  }, [url]);

  if (!texture || !scale || !url || !showReferenceImage) return null;

  const img = texture.image as { width?: number; height?: number } | undefined;
  const width = (img?.width ?? 0) * scale;
  const height = (img?.height ?? 0) * scale;
  if (!width || !height) return null;

  return (
    <mesh position={[0, 0, -0.01]} renderOrder={-1} userData={{ isReferenceImage: true }}>
      <planeGeometry args={[width, height]} />
      <meshBasicMaterial map={texture} transparent depthWrite={false} toneMapped={false} />
    </mesh>
  );
}
